import React, { useContext, useEffect, useState } from 'react';
import { DndProvider, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import ProductController, {
  OptionGroupItemType,
  OptionGroupType,
  OptionSoldType,
} from '../../../controller/ProductController';
import { useToast } from '../../../controller/ToastController';
import { AuthContext } from '../../../navigation/AuthProvider';
import { CurrentStoreContext } from '../../../controller/StoreController';

const OptionIndex = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSold, setIsSold] = useState<boolean>(false);
  const [optionList, setOptionList] = useState<OptionGroupType[]>([]);
  const [supplyOption, setSupplyOption] = useState<OptionSoldType[]>([]);
  const [searchedList, setSearchedList] = useState<OptionGroupType[]>([]);
  const { currentStore } = useOutletContext<CurrentStoreContext>();
  const [isOpen, setIsOpen] = useState<boolean[]>([]);
  const [keyword, setKeyword] = useState<string>('');

  const navigateOptionDetails = (option_index?: number) => {
    if (option_index !== undefined) {
      navigate(`/product/option/edit`, { state: { optionInfo: optionList[option_index] } });
    } else navigate(`/product/option/edit`);
  };

  // 검색기능
  const getSearchResult = (optionList: OptionGroupType[]) => {
    const filteredList = optionList
      .map((group) => ({
        ...group,
        option_list: group.option_list.filter((option) => option.name.includes(keyword)),
      }))
      .filter((group) => group.option_list.length > 0);

    setSearchedList(filteredList);
  };
  // 옵션 품절 처리
  const onChangeSold = () => {
    setIsSold(!isSold);
  };
  const onChangeSoldout = (mainIndex: number, subIndex: number, mainItem: OptionGroupType) => {
    let minSoldout = 0;
    const targetItem = optionList[mainIndex].option_list[subIndex];
    const updatedItem = {
      option_id: targetItem.option_id,
      is_soldout: targetItem.is_soldout === 1 ? 0 : 1,
      store_id: currentStore.value,
    };

    if (mainItem.input_min > 0) {
      minSoldout = mainItem.input_min;
    }

    const nonSoldOutCount = mainItem.option_list.filter((opt) => opt.is_soldout === 0).length;

    if (updatedItem.is_soldout === 1 && nonSoldOutCount <= minSoldout) {
      showToast(
        `필수선택이 ${minSoldout}개 이상이기 때문에 품절처리를 할 수 없습니다.`,
        'error',
      );
      return;
    }

    setSupplyOption((prevSupplyOption: any[]) => {
      const exists = prevSupplyOption.find((opt) => opt.option_id === updatedItem.option_id);

      if (exists) {
        return prevSupplyOption.filter((opt) => opt.option_id !== updatedItem.option_id);
      } else {
        return [...prevSupplyOption, updatedItem];
      }
    });

    setOptionList((prevOptionList) => {
      const updatedOptionList = [...prevOptionList];
      updatedOptionList[mainIndex].option_list[subIndex].is_soldout = updatedItem.is_soldout;
      return updatedOptionList;
    });
  };

  // API 호출
  const getOptions = async () => {
    try {
      const oRes = await ProductController.getAllOptionGroup(currentStore.value);

      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('getOptions', oResponse);
        if (oResponse.data) {
          setOptionList([...oResponse.data]);
          let openTemp = new Array(oResponse.data.length).fill(true);
          setIsOpen([...openTemp]);
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };
  const changeIndexOptionData = async () => {
    try {
      const oRes = await ProductController.editOptionGroupIdOrder(currentStore.value, optionList);
      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('editOptionGroupIdOrder', oResponse);
        if (oResponse.data && oResponse.ok) {
          showToast('옵션의 순서를 변경했어요');
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };
  const soldoutOption = async () => {
    try {
      const oRes = await ProductController.setSupplyOption(supplyOption);
      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('soldoutOption', oResponse);
        if (oResponse.data && oResponse.ok) {
          showToast('선택된 옵션이 품절되었습니다.');
          setIsSold(false);
          setSupplyOption([]);
          getOptions();
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };

  useEffect(() => {
    setSearchedList(optionList);
  }, [optionList]);

  useEffect(() => {
    getSearchResult(optionList);
  }, [keyword]);
  useEffect(() => {
    getOptions();
  }, [currentStore]);

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 1) toIndex = 0;
    else if (toIndex > optionList.length - 1) toIndex = optionList.length;
    const newItems = [...optionList];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    setOptionList(newItems);
    setIsOpen(new Array(optionList.length).fill(true));
  };

  interface DraggableItemProps {
    item: OptionGroupType;
    index: number;
    moveItem: (fromIndex: number, toIndex: number) => void;
  }

  const RenderOptionGroup = ({ item, index, moveItem }: DraggableItemProps) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(index + 1);

    const changeIsOpen = () => {
      const dataTemp = isOpen;
      dataTemp[index] = !dataTemp[index];
      setIsOpen([...dataTemp]);
    };

    const [, drag] = useDrag({
      type: 'ITEM',
      item: { item, index },
    });

    const [, drop] = useDrop({
      accept: 'ITEM',
      hover: (item: { index: number }, monitor: DropTargetMonitor) => {
        if (item.index !== index) {
          moveItem(item.index, index);
          item.index = index;
        }
      },
    });

    const moveMenuItem = (mainIndex: number, fromIndex: number, toIndex: number) => {
      if (toIndex < 1) toIndex = 0;
      else if (toIndex > optionList[mainIndex].option_list.length - 1)
        toIndex = optionList[mainIndex].option_list.length;
      let newMainItems = [...optionList];
      const newItems = [...newMainItems[mainIndex].option_list];
      const [movedItem] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, movedItem);
      newMainItems[mainIndex].option_list = newItems;
      setOptionList(newMainItems);
    };

    return (
      <div key={index}>
        <div className={`listItem ${isOpen[index] && 'open'}`}>
          <div className="row gap">
            <div className="row">
              <input
                ref={(node) => drag(drop(node))}
                value={selectedIndex}
                autoFocus={false}
                onChange={(e) => {
                  setSelectedIndex(Number(e.target.value));
                }}
                onBlur={() => {
                  moveItem(index, selectedIndex - 1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    moveItem(index, selectedIndex - 1);
                  }
                }}
              />
              {/* <img
                src={`${process.env.PUBLIC_URL}/svg/ic_arrow_up_box.svg`}
                onClick={() => orderUpChange(index)}
                alt="ic"
              />
              <img
                src={`${process.env.PUBLIC_URL}/svg/ic_arrow_down_box.svg`}
                onClick={() => orderDownChange(index)}
                alt="ic"
              /> */}
            </div>
            <h3>{item.name}</h3>
            {item.input_min !== 0 && <h5>필수선택 {item.input_min}개</h5>}
          </div>
          <div className="row gap">
            <img
              src={`${process.env.PUBLIC_URL}/svg/ic_edit.svg`}
              alt="ic"
              onClick={() => navigateOptionDetails(index)}
            />
            {/* <img src={`${process.env.PUBLIC_URL}/svg/ic_trashcan.svg`} alt="ic" /> */}
            <img
              src={`${process.env.PUBLIC_URL}/svg/ic_arrow_${
                isOpen[index] ? 'up' : 'down'
              }_grey.svg`}
              onClick={changeIsOpen}
              width={30}
              height={30}
              alt="ic"
            />
          </div>
        </div>
        {isOpen[index] && (
          <div className="menuList">
            {item.option_list.map((subItem: OptionGroupItemType, subIndex: number) => {
              return (
                <RenderOptionItem
                  key={`${index}_${subIndex}`}
                  mainIndex={index}
                  subItem={subItem}
                  subIndex={subIndex}
                  moveMenuItem={moveMenuItem}
                  mainItem={item}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  interface DraggableMenuItemProps {
    subItem: OptionGroupItemType;
    mainIndex: number;
    subIndex: number;
    mainItem: OptionGroupType;
    moveMenuItem: (mainIndex: number, fromIndex: number, toIndex: number) => void;
  }

  const RenderOptionItem = ({
    subItem,
    mainIndex,
    subIndex,
    moveMenuItem,
    mainItem,
  }: DraggableMenuItemProps) => {
    const [selectedMenuIndex, setSelectedMenuIndex] = useState<number>(subIndex + 1);

    const [, menuDrag] = useDrag({
      type: 'ITEM',
      item: { mainIndex, subItem, subIndex },
    });

    const [, menuDrop] = useDrop({
      accept: 'ITEM',
      hover: (item: { mainIndex: number; subIndex: number }, monitor: DropTargetMonitor) => {
        if (item.mainIndex === mainIndex && item.subIndex !== subIndex) {
          moveMenuItem(mainIndex, item.subIndex, subIndex);
          item.subIndex = subIndex;
        }
      },
    });

    return (
      <div className={`menuItem ${subItem.is_soldout === 1 && 'sold'}`} key={subIndex}>
        <div className="row">
          <input
            ref={(node) => menuDrag(menuDrop(node))}
            value={selectedMenuIndex}
            autoFocus={false}
            onChange={(e) => {
              setSelectedMenuIndex(Number(e.target.value));
            }}
            onBlur={() => {
              moveMenuItem(mainIndex, subIndex, selectedMenuIndex - 1);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                moveMenuItem(mainIndex, subIndex, selectedMenuIndex - 1);
              }
            }}
          />
          <div className="column">
            <h4>{subItem.name}</h4>
            <h5>{subItem.price}원</h5>
          </div>
        </div>
        <div className="row">
          <div className="soldText">{subItem.is_soldout === 1 && 'SOLD OUT'}</div>
          {isSold && (
            <input
              type="checkbox"
              value={subItem.is_soldout}
              autoFocus={false}
              checked={
                supplyOption.find((opt) => opt.option_id === subItem.option_id)?.is_soldout === 1 ||
                (supplyOption.find((opt) => opt.option_id === subItem.option_id) === undefined &&
                  subItem.is_soldout === 1)
              }
              onChange={() => onChangeSoldout(mainIndex, subIndex, mainItem)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <Style>
      <div className="contentContainer">
        <button className="addButton" onClick={() => navigateOptionDetails()}>
          <img src={`${process.env.PUBLIC_URL}/svg/ic_plus.svg`} alt="ic" />
          <h4>옵션 만들기</h4>
        </button>
        <div className="soldOutWrap">
          <div className="search">
            <input
              type="text"
              value={keyword}
              className="searchInput"
              placeholder="메뉴명을 검색해보세요"
              autoFocus={false}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  getSearchResult(optionList);
                }
              }}
              onBlur={() => getSearchResult(optionList)}
            />
            <button className="searchBox" onClick={() => getSearchResult(optionList)}>
              <img src={`${process.env.PUBLIC_URL}/svg/ic_search_grey.svg`} alt="search" />
            </button>
          </div>
          <button className={`soldoutButton ${isSold && 'active'}`} onClick={onChangeSold}>
            <h5>품절</h5>
          </button>
        </div>
        <DndProvider backend={HTML5Backend}>
          {searchedList.length > 0 ? (
            <div className="categoryList">
              {searchedList.map((item: OptionGroupType, index: number) => {
                return (
                  <RenderOptionGroup key={index} item={item} index={index} moveItem={moveItem} />
                );
              })}
            </div>
          ) : (
            <div className="noneWrap">
              <h3>옵션을 등록해주세요</h3>
            </div>
          )}
        </DndProvider>
        <button className={`submitButton`} onClick={isSold ? soldoutOption : changeIndexOptionData}>
          저장
        </button>
      </div>
    </Style>
  );
};

export default OptionIndex;

const Style = styled.div`
  @keyframes expandDown {
    0% {
      height: 0;
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .addButton {
    width: 100%;
    border: 1px solid #4dd2ae;
    border-radius: 5px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5%;
    margin-bottom: 10px;

    h4 {
      font-weight: 600;
      color: #4dd2ae;
      white-space: nowrap;
      padding: 0;
    }
  }

  input {
    width: 30px;
    height: 30px;
    padding: 4px;
    font-size: 16px;
    line-height: 20px;
    font-weight: 600;
    text-align: center;
    color: #40977f;
    background: #ecf5f3;
    border: 1px solid #4dd2ae;
    border-radius: 4px;
  }

  .categoryList {
    padding: 10px 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .listItem {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 16px 10px 22px;
      background: #ecf5f3;
      border: 1px solid #ecf5f3;
      border-radius: 5px;

      .row {
        gap: 6px;
      }
      .row.gap {
        gap: 14px;
      }
      h3 {
        color: #555555;
        font-weight: 600;
      }
      h5 {
        color: #979797;
        font-weight: 500;
      }
    }
    .listItem.open {
      border-radius: 5px 5px 0 0;
    }

    .menuList {
      display: flex;
      flex-direction: column;
      border: 1px solid #e5e5e5;
      border-top: none;
      border-radius: 0 0 5px 5px;

      .menuItem {
        min-height: 65px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 2px 22px;
        border-bottom: 1px solid #f7f7f7;

        input {
          width: 24px;
          height: 24px;
          padding: 4px;
          font-size: 14px;
          line-height: 16px;
          color: #979797;
          background: #ffffff;
          border: 1px solid #cecece;
        }

        .column {
          gap: 2px;
        }
        h4 {
          color: #555555;
          font-weight: 600;
        }
        h5 {
          color: #555555;
          font-weight: 400;
        }
        .row {
          gap: 20px;

          .imgWrap {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 60px;
            height: 60px;
            border-radius: 5px;
            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              border-radius: 5px;
            }
          }
        }
      }
      .menuItem.sold {
        background: #e5e5e5;
        padding-bottom: 0;
      }
    }
  }
  .soldOutWrap {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    .search {
      flex: 1;
      width: 100%;
      position: relative;
    }
    .searchBox {
      top: 0;
      padding: 8px 0;
      position: absolute;
      display: flex;
      align-items: center;
      cursor: pointer;
      right: 14px;
    }
    .searchInput {
      width: 100%;
      padding: 20px;
      background: #f7f7f7 !important;
      border: 1px solid #cecece;
      text-align: left;
      color: gray;
    }
    input::placeholder {
      font-weight: 400;
      color: #cecece;
    }
    .soldoutButton {
      padding: 10px 20px;
      border: 1px solid #cecece;
      background: #f7f7f7;
      border-radius: 5px;
      font-size: 14px;
      font-weight: 500;
      color: #555;
    }
  }
  .soldText {
    font-size: 14px;
    font-weight: 500;
    color: #ff6060;
  }
  input[type='checkbox'] {
    accent-color: #40977f;
    width: 16px !important;
    height: 16px !important;
    vertical-align: middle;
    cursor: pointer;
  }

  @media (max-width: 600px) {
    .categoryList {
      gap: 20px;

      .listItem {
        padding: 8px 10px 8px 16px;

        input {
          width: 26px;
          height: 26px;
          font-size: 14px;
        }
        .row.gap {
          gap: 6px;
        }

        h3 {
          font-size: 16px;
        }
        h5 {
          font-size: 10px;
          white-space: nowrap;
          padding-right: 6px;
        }
      }

      .menuList {
        .menuItem {
          min-height: 50px;
          padding: 2px 16px;

          .setBadge {
            padding: 2px 8px;
            font-size: 12px;
            line-height: 14px;
          }
          input {
            width: 22px;
            height: 22px;
            padding: 4px;
            font-size: 12px;
            line-height: 14px;
          }

          .column {
            .row {
              gap: 6px;
            }
          }
          h4 {
            font-size: 14px;
            color: #555555;
            font-weight: 600;
          }
          h5 {
            font-size: 12px;
            color: #555555;
            font-weight: 400;
          }
          .row {
            gap: 14px;
            .imgWrap {
              width: 46px;
              height: 46px;
            }
          }
        }
      }
    }
  }
`;
